import { useState, useEffect } from 'react';

interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}
type CookiesReturnType = {
  cookies: { [key: string]: string }; // cookies property
  setCookie: (name: string, value: string, options?: CookieOptions) => void; // setCookie function
  getCookie: (name: string) => string | undefined; // getCookie function
  deleteCookie: (name: string) => void; // deleteCookie function
};

export function useCookies(): CookiesReturnType {
  const [cookies, setCookies] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const cookieString = document.cookie;
    const cookieArray = cookieString.split('; ');

    const cookieData: { [key: string]: string } = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const cookie of cookieArray) {
      const [name, value] = cookie.split('=');
      cookieData[name] = decodeURIComponent(value);
    }
    setCookies(cookieData);
  }, []);

  const setCookie = (name: string, value: string, options: CookieOptions = {}) => {
    const cookieOptions = [];

    if (options.expires) {
      if (options.expires instanceof Date) {
        cookieOptions.push(`expires=${options.expires.toUTCString()}`);
      } else {
        const expires = new Date();
        expires.setTime(expires.getTime() + options.expires * 1000);
        cookieOptions.push(`expires=${expires.toUTCString()}`);
      }
    }

    if (options.path) {
      cookieOptions.push(`path=${options.path}`);
    }

    if (options.domain) {
      cookieOptions.push(`domain=${options.domain}`);
    }

    if (options.secure) {
      cookieOptions.push('secure');
    }

    if (options.sameSite) {
      cookieOptions.push(`samesite=${options.sameSite}`);
    }

    const cookieString = `${name}=${encodeURIComponent(value)}; ${cookieOptions.join('; ')}`;
    document.cookie = cookieString;
    setCookies({ ...cookies, [name]: value });
  };

  const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift();
    }
    return undefined;
  };

  const deleteCookie = (name: string) => {
    // Set the cookie to expire immediately
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Remove it from the in-memory state as well
    const newCookies = { ...cookies };
    delete newCookies[name];
    setCookies(newCookies);
  };

  return { cookies, setCookie, getCookie, deleteCookie };
}
export default useCookies;
